import { computed, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { IUser, IUserDocs, ICountry, IPixKey, IHttpResponse, WALLET_TYPE } from '@libs/interfaces';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { IsValidCpf } from '@libs/helper/formatting.helper';
import { assign } from 'lodash';
import { MapResponseData } from '@libs/rxjs/operators.rxjs';
import { IToast, MODAL_TYPE } from '@libs/store/toast/model';
import { LangService } from '../lang.service';
import { ApiService } from '../api.service';
import { ScheduleService } from '../bicho/schedule-selector.service';
import { WalletService } from '../wallet/wallet.service';
import { UserStoreService } from '../store/user-store.service';
import { StoreToastModalsService } from '../store/store-toast-modals.service';

@Injectable({ providedIn: 'root' })
export class UserService {
  constructor(
    private langService: LangService,
    private apiService: ApiService,
    private userStoreService: UserStoreService,
    private walletService: WalletService,
    private scheduleService: ScheduleService,
    private storeToastService: StoreToastModalsService,
  ) {}

  /**
   * Fetches the user data from the API and updates the store and Tidio service.
   * @returns {Observable<IUser>} An observable containing the user data.
   */
  fetchUser(): Observable<IUser> {
    return this.apiService.get('user').pipe(
      MapResponseData,
      filter(Boolean),
      tap((user: IUser) => this.storeUserData(user)),
    );
  }

  /**
   * Updates the user image ID in the store.
   * @returns {Observable<void>} An observable that completes when the update is done.
   */
  updateUserImageId() {
    this.userStoreService.updateUserImageId(this.userStoreService.user()._id);
    this.onStoreUserData(this.userStoreService.user());
    this.storeAvatarUrl(assign({}, this.userStoreService.user(), { image_id: this.userStoreService.user()._id }));
  }

  /**
   * Stores the user data in the store and updates the selected wallet.
   * @param {IUser} user - The user data.
   * @returns {Observable<IUser>} An observable containing the user data.
   */
  private storeUserData(user: IUser) {
    this.storeAvatarUrl(user);
    this.langService.use(user.settings.lang);
    this.onStoreUserData(user);
    const currency =
      user.settings.wallet_type == WALLET_TYPE.CRYPTO || user.settings.wallet_type == WALLET_TYPE.TOKEN
        ? user.settings.crypto_currency
        : user.settings.currency;
    this.walletService.updateSelectedWallet(currency);
    this.scheduleService.onLogedSetSchedule(user.settings.draw_schedule);
  }

  /**
   * Stores the avatar URL in the store.
   * @param {IUser} user - The user data.
   */
  private storeAvatarUrl(user: IUser) {
    this.userStoreService.addAvatarUrl(user.avatarUrl);
  }

  /**
   * Updates the avatar image for the user.
   * Dispatches actions to set the avatar image and update the avatar URL in the store.
   */
  updateAvatarImage() {
    this.userStoreService.setAvatarImage();
    this.userStoreService.addAvatarUrl(
      `https://lotodobicho.com/avatars/${this.userStoreService.user()._id}.jpg?ts=${Date.now()}`,
    );
  }

  /**
   * Displays a warning message when documents cannot be activated.
   * Creates a toast notification and dispatches an action to add it to the store.
   */
  msgDocsCanntActivate() {
    const toast: IToast = {
      id: Date.now(),
      info: 'ACCOUNT.adecdp',
      type: MODAL_TYPE.WARNING,
      exp: 5000,
    };
    this.storeToastService.addToast(toast);
  }

  /**
   * Displays a warning message when documents cannot be activated.
   * Creates a toast notification and dispatches an action to add it to the store.
   */
  msgWithdrawDocsCanntActivate() {
    const toast: IToast = {
      id: Date.now(),
      info: 'ACCOUNT.MWD',
      type: MODAL_TYPE.WARNING,
      exp: 5000,
    };
    this.storeToastService.addToast(toast);
  }
  /**
   * Displays a warning message when documents cannot be activated.
   * Creates a toast notification and dispatches an action to add it to the store.
   */
  msgTelegramConnectedCanntActivate() {
    const toast: IToast = {
      id: Date.now(),
      info: 'ACCOUNT.MTWD',
      type: MODAL_TYPE.WARNING,
      exp: 5000,
    };
    this.storeToastService.addToast(toast);
  }
  /**
   * Adds a game bookmark for the user.
   * @param {string} game_id - The game ID.
   * @returns {Observable<void>} An observable that completes when the bookmark is added.
   */
  addGameBookmark(game_id: string) {
    return this.apiService
      .get(`user/bookmark/game/add/${game_id}`)
      .pipe(tap(() => this.userStoreService.addGameBookmark(game_id)));
  }

  /**
   * Removes a game bookmark for the user.
   * @param {string} game_id - The game ID.
   * @returns {Observable<void>} An observable that completes when the bookmark is removed.
   */
  removeGameBookmark(game_id: string) {
    return this.apiService
      .get(`user/bookmark/game/remove/${game_id}`)
      .pipe(tap(() => this.userStoreService.removeGameBookmark(game_id)));
  }

  /**
   * Saves the PIX key for the user.
   * @param {IPixKey} pixKey - The PIX key.
   * @returns {Observable<IHttpResponse>} An observable containing the response.
   */
  savePicKey(pixKey: IPixKey): Observable<IHttpResponse> {
    return this.apiService.post(`user/pixKey`, pixKey).pipe(tap(() => this.userStoreService.updatePixKey(pixKey)));
  }

  /**
   * Fetches the user documents from the API.
   * @returns {Observable<IUserDocs>} An observable containing the user documents.
   */
  fetchUserDocs(): Observable<IUserDocs> {
    return this.apiService.get('fetchUserDocs').pipe(MapResponseData);
  }

  /**
   * Updates the username for the user.
   * @param {string} username - The new username.
   * @returns {Observable<string>} An observable containing the updated username.
   */
  updateUsername(username: string): Observable<string> {
    return this.apiService.patch(`user/username/update/${username}`).pipe(
      MapResponseData,
      tap(() => this.userStoreService.setUsername(username)),
    );
  }

  /**
   * Updates the email for the user.
   * @param {string} email - The new email.
   * @returns {Observable<string>} An observable containing the updated email.
   */
  updateEmail(email: string): Observable<string> {
    return this.apiService.patch(`user/email/update/${email}`).pipe(
      MapResponseData,
      tap(() => this.setUserEmail(email)),
      tap(() => this.onUpdated()),
    );
  }

  /**
   * Checks if the username is free.
   * @param {string} username - The username to check.
   * @returns {Observable<boolean>} An observable containing the result.
   */
  isUsernameFree(username: string): Observable<boolean> {
    return this.apiService.get(`user/username/free/${username}`).pipe(
      catchError(() => of({ data: true })),
      MapResponseData,
    );
  }

  /**
   * Checks if the email is free.
   * @param {string} email - The email to check.
   * @returns {Observable<boolean>} An observable containing the result.
   */
  isEmailFree(email: string): Observable<boolean> {
    return this.apiService.get(`user/email/free/${email}`).pipe(
      catchError(() => of({ data: true })),
      MapResponseData,
    );
  }

  /**
   * Sends a verification email to the user.
   * @param {string} email - The email to send the verification to.
   * @returns {Observable<boolean>} An observable containing the result.
   */
  sendverificationEmail(email: string): Observable<boolean> {
    return this.apiService.get(`user/email/confirm/${email}`);
  }

  /**
   * Saves the user profile data.
   * @param {any} data - The profile data to save.
   * @returns {Observable<boolean>} An observable containing the result.
   */
  saveUserProfileData(data): Observable<boolean> {
    return this.apiService.post('user/profile/update', data).pipe(
      MapResponseData,
      tap((user: IUser) => this.onStoreUserData(user)),
      tap(() => this.saveToast()),
      map(Boolean),
    );
  }

  /**
   * Stores the user data in the store and updates the local user signal.
   * @param {IUser} user - The user data to be stored.
   * @private
   */
  private onStoreUserData(user: IUser) {
    this.userStoreService.setUser(user);
  }

  /**
   * Checks if the CPF exists.
   * @param {string} cpf - The CPF to check.
   * @returns {Observable<boolean>} An observable containing the result.
   */
  checkIfCpfExist(cpf): Observable<boolean> {
    if (!cpf) return of(false);
    return IsValidCpf(cpf)
      ? this.apiService.get(`users/cpf/exist/${cpf}`).pipe(
          MapResponseData,
          map(exist => !exist),
        )
      : of(false);
  }

  /**
   * Checks if the username exists.
   * @param {string} userName - The username to check.
   * @returns {Observable<boolean>} An observable containing the result.
   */
  checkIfUsernameExists(userName: string): Observable<boolean> {
    return this.apiService.get(`users/username/exist/:userName/${userName}`).pipe(MapResponseData);
  }

  /**
   * Checks if the phone number exists.
   * @param {string} phone - The phone number to check.
   * @param {string} phonePrefix - The phone prefix.
   * @returns {Observable<boolean>} An observable containing the result.
   */
  checkIfPhoneExists(phone: string, phonePrefix: string): Observable<boolean> {
    return this.apiService.get(`users/phone/exist/${phonePrefix}/${phone}`).pipe(MapResponseData);
  }

  /**
   * Checks if the email exists.
   * @param {string} email - The email to check.
   * @returns {Observable<boolean>} An observable containing the result.
   */
  checkIfEmailExists(email: string): Observable<boolean> {
    return this.apiService.get(`users/email/exist/${email}`).pipe(MapResponseData);
  }

  /**
   * Gets the country from the user's IP address.
   * @returns {Observable<ICountry>} An observable containing the country data.
   */
  getCountryFromIp(): Observable<ICountry> {
    return this.apiService.get('getCountryFromIp').pipe(MapResponseData);
  }

  /**
   * Gets the phone prefix for the user.
   * @returns {Observable<string>} An observable containing the phone prefix.
   */
  getPhonePrefix(): Observable<number> {
    return this.apiService.get('auth/user/phonePrefix').pipe(
      MapResponseData,
      map(val => +val),
    );
  }

  /**
   * Sets the user email in the store.
   * @param {string} email - The email to set.
   */
  setUserEmail(email: string) {
    this.userStoreService.setUserEmail(email);
  }

  /**
   * Displays a success toast message.
   * Creates a toast notification and dispatches an action to add it to the store.
   */
  saveToast() {
    const toast: IToast = {
      id: Date.now(),
      info: 'ACCOUNT.DDCC',
      type: MODAL_TYPE.SUCCESS,
      exp: 2000,
    };
    this.storeToastService.addToast(toast);
  }

  /**
   * Displays a success toast message when the user is updated.
   * Creates a toast notification and dispatches an action to add it to the store.
   */
  onUpdated() {
    const toast = {
      id: Date.now(),
      info: 'ACCOUNT.CETC',
      type: MODAL_TYPE.SUCCESS,
    };
    this.storeToastService.addToast(toast);
  }

  /**
   * Selects the user state from the store.
   * @returns {Observable<ENUM_USER_DATA_STATE>} An observable containing the user state.
   */
  selectUserState() {
    return this.userStoreService.selectUserState();
  }

  /**
   * Selects the avatar URL from the store.
   * @returns {Observable<string>} An observable containing the avatar URL.
   */
  selectAvatar() {
    return this.userStoreService.selectAvatar();
  }
}
